import { ReactNode } from 'react';
import { OverrideType } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import PhoneTreeAPI from '@frontend/api-phone-trees';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Button,
  ModalControlResponse,
  PlusIconSmall,
  SearchIcon,
  Text,
  TextLink,
  styles,
  useTooltip,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import {
  emptyOverrideSectionStyle,
  overrideSectionChipStyle,
  overrideSectionHeadingStyle,
} from '../../views/settings/overrides-styles';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';
import useVoicemailBoxes from '../voicemail-box/use-voicemail-boxes';

export const EmptyOverrideSection = ({
  openSettingTray,
  className,
  isSideModal,
  editOverride,
  hasUserManagementPermission,
}: {
  openSettingTray: () => void;
  className?: string;
  isSideModal?: boolean;
  editOverride?: ModalControlResponse;
  hasUserManagementPermission?: boolean;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });

  return (
    <>
      {isSideModal ? (
        <section
          css={{
            padding: theme.spacing(3),
            background: theme.colors.neutral5,
            border: `1px solid ${theme.colors.neutral20}`,
            borderRadius: theme.borderRadius.medium,
          }}
        >
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), alignItems: 'center' }}>
            <SearchIcon size={24} css={{ color: theme.colors.neutral70 }} />
            <Text size='large' weight='bold'>
              {t('No Active Override')}
            </Text>

            {hasUserManagementPermission && (
              <Button
                variant='secondary'
                size='large'
                onClick={editOverride?.openModal}
                trackingId='phone-portal-btn-openOverrideModal'
                css={{ color: theme.colors.neutral70 }}
              >
                <PlusIconSmall /> {t('Create Override')}
              </Button>
            )}
          </div>
        </section>
      ) : (
        <section css={emptyOverrideSectionStyle} className={className}>
          <div css={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
            <SearchIcon size={16} color='subdued' />
            <Text size='medium' color='light'>
              {t('No Active Override')}
            </Text>
          </div>
          {hasUserManagementPermission && (
            <TextLink
              onClick={openSettingTray}
              css={{ display: 'flex', gap: theme.spacing(1), width: 'auto', alignItems: 'center' }}
              trackingId='phone-portal-btn-openOverrideModal'
              weight='bold'
            >
              <PlusIconSmall /> {t('Create Override')}
            </TextLink>
          )}
        </section>
      )}
    </>
  );
};

type OverrideSectionProps = {
  type: OverrideType;
  statusChip: ReactNode;
  startTime?: string;
  endTime?: string;
  mailboxId?: string;
  media?: {
    id: string;
    name: string;
    path: string;
  };
  ivrMenuId?: string;
  forwardingNumber?: ForwardingNumberTypes.FwdNumberModel;
  openRemoveModal: () => void;
  openSettingModal: () => void;
  className?: string;
  isSideModal?: boolean;
  editOverride?: ModalControlResponse;
  removeOverride?: ModalControlResponse;
  hasUserManagementPermission?: boolean;
};

const GridArea = ({ area, title, children }: { area: string; title: ReactNode; children: ReactNode }) => (
  <div css={{ gridArea: area }}>
    <Text as='dt' size='small' color='light'>
      {title}
    </Text>
    <Text
      size='medium'
      as='dd'
      css={[title !== 'Forwarding Number' && styles.truncate, { lineHeight: theme.spacing(4), maxWidth: '240px' }]}
    >
      {children}
    </Text>
  </div>
);

export const OverrideSection = ({
  type,
  statusChip,
  startTime,
  endTime,
  mailboxId,
  media,
  forwardingNumber,
  openRemoveModal,
  openSettingModal,
  ivrMenuId,
  className,
  isSideModal,
  editOverride,
  removeOverride,
  hasUserManagementPermission,
}: OverrideSectionProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const locationData = accessibleLocationData[selectedLocationIds[0]];

  const { data: voicemailBoxes = [] } = useVoicemailBoxes({
    phoneTenantId: locationData.phoneTenantId ?? '',
  });

  const { data: phoneTreeData } = useQuery({
    queryKey: [locationData.phoneTenantId, ...queryKeys.settings.readPhoneTree(ivrMenuId ?? '')],
    queryFn: () => PhoneTreeAPI.Read({ phoneTreeId: ivrMenuId ?? '' }),
    enabled: !!locationData.phoneTenantId && !!ivrMenuId,
    meta: {
      error: {
        message: 'OverrideSection:phoneTreeDataQuery | Failed to fetch phone tree',
        data: { phoneTenantId: locationData.phoneTenantId, ivrMenuId },
      },
    },
  });

  const voicemailBox = voicemailBoxes?.filter((vm) => vm.mailbox.id === mailboxId);

  const { triggerProps: editTriggerProps, tooltipProps: editTooltipProps, Tooltip: EditTooltip } = useTooltip();
  const { triggerProps: removeTriggerProps, tooltipProps: removeTooltipProps, Tooltip: RemoveTooltip } = useTooltip();
  const tooltipText = t('You do not have the necessary permissions to take this action.');

  return (
    <>
      <section
        className={className}
        css={
          isSideModal
            ? {
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                padding: theme.spacing(3),
                background: theme.colors.white,
                border: `1px solid ${theme.colors.neutral20}`,
                borderRadius: theme.borderRadius.medium,
              }
            : undefined
        }
      >
        {!isSideModal && (
          <Text color={'light'} size='small'>
            {t('Override Type')}
          </Text>
        )}
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Text weight='bold' css={[overrideSectionHeadingStyle, isSideModal && { alignItems: 'flex-start' }]}>
            <div style={{ flex: 1 }}>
              <div
                css={[overrideSectionChipStyle, isSideModal && { flexDirection: 'column', alignItems: 'flex-start' }]}
              >
                {type === OverrideType.OVERRIDE_TYPE_VMO
                  ? t('Send to Voicemail')
                  : type === OverrideType.OVERRIDE_TYPE_FWD
                  ? t('Forward Call')
                  : type === OverrideType.OVERRIDE_TYPE_IVR
                  ? t('Send to Phone Tree')
                  : 'Unknown Type'}{' '}
                {statusChip}
              </div>{' '}
            </div>
            {isSideModal ? (
              <div style={{ display: 'flex', gap: theme.spacing(1.5) }}>
                <Button
                  {...removeTriggerProps}
                  disabled={!hasUserManagementPermission}
                  variant='secondary'
                  destructive
                  css={{ width: 'auto' }}
                  onClick={() => removeOverride?.openModal()}
                  trackingId='phone-portal-btn-removeOverride'
                >
                  <Icon name='remove2-small' />
                  {t('Remove')}
                </Button>
                {!hasUserManagementPermission && (
                  <RemoveTooltip {...removeTooltipProps} css={{ width: theme.spacing(29) }}>
                    {tooltipText}
                  </RemoveTooltip>
                )}{' '}
                <Button
                  {...editTriggerProps}
                  disabled={!hasUserManagementPermission}
                  variant='tertiary'
                  css={{ color: theme.colors.neutral90, padding: theme.spacing(0.5) }}
                  onClick={() => {
                    editOverride?.openModal();
                  }}
                >
                  <Icon name='edit-small' />
                </Button>
                {!hasUserManagementPermission && (
                  <EditTooltip {...editTooltipProps} css={{ width: theme.spacing(29) }}>
                    {tooltipText}
                  </EditTooltip>
                )}{' '}
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: theme.spacing(2) }}>
                  <Button
                    {...editTriggerProps}
                    variant='secondary'
                    style={{ width: 'auto' }}
                    onClick={() => openSettingModal()}
                    trackingId='phone-portal-btn-openEditOverride'
                    disabled={!hasUserManagementPermission}
                  >
                    <Icon name='edit-small' />
                    {t('Edit')}
                  </Button>
                  {!hasUserManagementPermission && (
                    <EditTooltip {...editTooltipProps} css={{ width: theme.spacing(29) }}>
                      {tooltipText}
                    </EditTooltip>
                  )}{' '}
                  <Button
                    {...removeTriggerProps}
                    variant='secondary'
                    destructive
                    css={{ width: 'auto' }}
                    onClick={() => openRemoveModal()}
                    trackingId='phone-portal-btn-removeOverride'
                    disabled={!hasUserManagementPermission}
                  >
                    <Icon name='remove2-small' />
                    {t('Remove')}
                  </Button>
                </div>
                {!hasUserManagementPermission && (
                  <RemoveTooltip {...removeTooltipProps} css={{ width: theme.spacing(29) }}>
                    {tooltipText}
                  </RemoveTooltip>
                )}{' '}
              </div>
            )}
          </Text>

          <dl
            css={{
              display: 'flex',
              gap: isSideModal ? theme.spacing(2) : theme.spacing(2),
              ...(isSideModal && { flexDirection: 'column' }),
            }}
          >
            {' '}
            {type === OverrideType.OVERRIDE_TYPE_VMO && mailboxId && (
              <GridArea area='yoyo' title={t('Voicemail Box')}>
                {voicemailBox?.[0]?.mailbox?.name}
              </GridArea>
            )}
            {type === OverrideType.OVERRIDE_TYPE_VMO && (
              <GridArea area='type' title={t('Voicemail Greeting')}>
                {media?.name}
              </GridArea>
            )}
            {type === OverrideType.OVERRIDE_TYPE_VMO && media?.name && media.name !== 'No Greeting' && (
              <GridArea area='media' title={t('Audio File')}>
                <CachedAudioScrubber
                  filePath={media?.id ? media.path : ''}
                  mediaId={media?.id ? media.id : 'default'}
                />
              </GridArea>
            )}
            {type === OverrideType.OVERRIDE_TYPE_FWD && (
              <GridArea area='type' title={t('Forwarding Number')}>
                {`${forwardingNumber?.name} (${forwardingNumber?.number})`}
              </GridArea>
            )}
            {type === OverrideType.OVERRIDE_TYPE_IVR && (
              <GridArea area='type' title={t('Phone Tree')}>
                {phoneTreeData?.name}
              </GridArea>
            )}
            {type === OverrideType.OVERRIDE_TYPE_IVR && (
              <GridArea area='type' title={t('Informational Message')}>
                {media?.name || t('No Message')}
              </GridArea>
            )}
            {type === OverrideType.OVERRIDE_TYPE_IVR && media?.id && media?.path && (
              <GridArea area='media' title={t('Audio File')}>
                <CachedAudioScrubber
                  filePath={media.path}
                  mediaId={media.id}
                  trackingId='nwx-override-card-informational-message'
                />
              </GridArea>
            )}
          </dl>
          {(startTime || endTime) && (
            <dl css={{ display: 'flex', gap: theme.spacing(2) }}>
              {/* We also check if officeTimezone is defined b/c we don't want to show the wrong tz */}
              {startTime && (
                <GridArea area='start' title={t('Start Time')}>
                  {startTime}
                </GridArea>
              )}
              {/* We also check if officeTimezone is defined b/c we don't want to show the wrong tz */}
              {endTime && (
                <GridArea area='end' title={t('End Time')}>
                  {endTime}
                </GridArea>
              )}
            </dl>
          )}
        </div>
      </section>
    </>
  );
};
